import { z } from "zod";

import { PromptJsonSchema } from "../../../../schemas/Agent/promptJson.schema";
import { PromptVersionType } from "../../../../shared/domain/agents/prompt-version.types";

export const AGENT_PROMPT_VERSION_TYPE_VALUES = ["CHECKPOINT", "AUTO"] as const;
export const PromptVersionTypeSchema = z.nativeEnum(PromptVersionType);

export const VersionCreatorSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable()
});

/**
 * VersionMetadataSchema
 *
 * Version summary used in list responses and mutation responses.
 * No promptJson — use VersionDetailSchema for full content.
 */
export const VersionMetadataSchema = z.object({
  id: z.string(),
  type: PromptVersionTypeSchema,
  name: z.string().nullable(),
  description: z.string().nullable(),
  // ISO string to stay compatible with existing PromptVersionMetadata interface
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  creator: VersionCreatorSchema.nullable(),
  isProduction: z.boolean(),
  autoSaveCountBefore: z.number().optional(),
  prevCheckpointAt: z.string().nullable().optional()
});

export type VersionMetadata = z.infer<typeof VersionMetadataSchema>;

/**
 * VersionDetailSchema
 *
 * Full version data including promptJson. Used for getVersion and restore responses.
 */
export const VersionDetailSchema = z.object({
  id: z.string(),
  type: PromptVersionTypeSchema,
  name: z.string().nullable(),
  description: z.string().nullable(),
  promptJson: PromptJsonSchema,
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  creator: VersionCreatorSchema.nullable(),
  isProduction: z.boolean()
});

export type VersionDetail = z.infer<typeof VersionDetailSchema>;

/**
 * VersionListResponseSchema
 *
 * Paginated list of version metadata.
 */
export const VersionListResponseSchema = z.object({
  versions: z.array(VersionMetadataSchema),
  total: z.number(),
  /** Count of CHECKPOINT versions — used for auto-naming new versions (v1, v2, v3...) */
  checkpointCount: z.number()
});

export type VersionListResponse = z.infer<typeof VersionListResponseSchema>;

/**
 * VersionStatusSchema
 *
 * Lightweight status for the TipTap editor header.
 */
export const VersionStatusSchema = z.object({
  checkpointCount: z.number(),
  productionVersionName: z.string().nullable(),
  /** true = current draft (promptJson) is the production version (productionPromptVersionId is null) */
  isDraftProduction: z.boolean()
});

export type VersionStatus = z.infer<typeof VersionStatusSchema>;
